import React from 'react';
import MultiStepForm from './components/MultiStepForm';
import Library from './components/Library';
import Login from './components/Login';
import Logs from './components/Logs';
import Webhook from './components/Webhook';
import WebhookRender from './components/WebhookRender';
import { useEffect, useState } from 'react';
import { supabase } from './lib/supabaseClient';
import { logger } from './lib/loggingService';
import { webhookLogsApi } from './lib/dbApi';
import './App.css';

const Icon = {
  Home: (p: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={p.className}><path d="M3 12l9-9 9 9" /><path d="M5 10v10a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1V10" /></svg>
  ),
  Folder: (p: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={p.className}><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2z" /></svg>
  ),
  Share: (p: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={p.className}><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="M8.6 13.5l6.8 3.9M15.4 6.6L8.6 10.5" /></svg>
  ),
  LogOut: (p: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={p.className}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="M16 17l5-5-5-5" /><path d="M21 12H9" /></svg>
  ),
  Zap: (p: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={p.className}><path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" /></svg>
  ),
  Shield: (p: { className?: string }) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={p.className}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
  ),
};

const NAV_ITEMS = [
  { to: '/', label: 'Home', icon: Icon.Home },
  { to: '/library', label: 'Library', icon: Icon.Folder },
  { to: '/webhook', label: 'Webhook', icon: Icon.Share },
];

function App() {
  const [isAuthed, setIsAuthed] = useState<boolean | null>(null);
  const [path, setPath] = useState<string>(() => window.location.pathname);
  const [userEmail, setUserEmail] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const [webhookData, setWebhookData] = useState<any>(null);
  // Webhook notifications only make sense before a report is being generated/edited -
  // hide the panel once MultiStepForm has moved past its initial data-collection step.
  const [formStep, setFormStep] = useState<'scrape' | 'form'>('scrape');
  const [webhookUnreadCount, setWebhookUnreadCount] = useState(0);

  useEffect(() => {
    const getSession = async () => {
      const { data } = await supabase.auth.getSession();
      setIsAuthed(!!data.session);
      if (data.session?.user?.email) {
        setUserEmail(data.session.user.email);
        setUserName(data.session.user.user_metadata?.name || '');
      }
    };
    getSession();
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthed(!!session);
      if (session?.user?.email) {
        setUserEmail(session.user.email);
        setUserName(session.user.user_metadata?.name || '');
      } else {
        setUserName('');
      }
    });
    
    // Load webhook data from localStorage if available
    try {
      const storedWebhookData = localStorage.getItem('webhookData');
      if (storedWebhookData) {
        setWebhookData(JSON.parse(storedWebhookData));
        localStorage.removeItem('webhookData');
      }
    } catch (e) {
      console.error('Error loading webhook data:', e);
    }

    // Load edit context from localStorage
    try {
      const editContext = localStorage.getItem('edit_context');
      if (editContext) {
        const context = JSON.parse(editContext);
        setWebhookData({ 
          isEditing: true, 
          reportId: context.reportId,
          editingClientId: context.clientId 
        });
        localStorage.removeItem('edit_context');
      }
    } catch (e) {
      console.error('Error loading edit context:', e);
    }
    
    // Clear webhook data on page refresh to prevent data leakage to other reports
    const handleBeforeUnload = () => {
      try {
        localStorage.removeItem('webhookData');
        localStorage.removeItem('webhookDataSource');
      } catch (e) {
        console.error('Error clearing webhook data on unload:', e);
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    const onPop = () => setPath(window.location.pathname);
    const onNavigate = (e: any) => {
      const to = e?.detail?.to || '/';
      if (window.location.pathname !== to) {
        window.history.pushState({}, '', to);
        setPath(to);
      }
    };
    window.addEventListener('popstate', onPop);
    window.addEventListener('app:navigate', onNavigate as any);
    
    // Global error handler
    const handleError = async (event: ErrorEvent) => {
      if (userEmail) {
        await logger.logFrontendError(
          userEmail,
          'UNCAUGHT_ERROR',
          event.message,
          event.error?.stack,
          {
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno
          }
        );
      }
    };
    
    const handleUnhandledRejection = async (event: PromiseRejectionEvent) => {
      if (userEmail) {
        await logger.logFrontendError(
          userEmail,
          'UNHANDLED_REJECTION',
          event.reason?.message || 'Promise rejected',
          event.reason?.stack
        );
      }
    };
    
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      sub.subscription.unsubscribe();
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('app:navigate', onNavigate as any);
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [userEmail]);

  // Poll the unread webhook count for the sidebar badge - the Webhook page itself
  // (not this dashboard) is where you actually read/act on them.
  useEffect(() => {
    if (!isAuthed) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { total } = await webhookLogsApi.list({ viewed: false, limit: 1 });
        if (!cancelled) setWebhookUnreadCount(total || 0);
      } catch (e) {
        // Non-critical - just leave the badge as-is on a transient failure.
      }
    };
    poll();
    const interval = setInterval(poll, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isAuthed]);

  const otpPending = (() => { try { return localStorage.getItem('otp_pending') === '1'; } catch { return false; } })();

  // Webhook rendering route (used by puppeteer). Must not require auth.
  if (path === '/webhook-render') {
    return <WebhookRender />;
  }

  const navigate = (to: string) => window.dispatchEvent(new CustomEvent('app:navigate', { detail: { to } }));
  const showChrome = isAuthed && !otpPending;
  // Once you're actively building a report, the sidebar just eats space you need
  // for the 3-column step editor - hide it and let the app go full width.
  const showSidebar = showChrome && !(path === '/' && formStep === 'form');
  const displayName = userName || userEmail;
  const initials = ((userName || userEmail.split('@')[0]) || '?').slice(0, 2).toUpperCase();

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      {/* Header */}
      <header className="bg-[#722420] text-white shrink-0">
        <div className="flex items-center justify-between px-4 sm:px-6 py-3">
          <button
            onClick={() => navigate('/')}
            className="flex-shrink-0 flex items-center gap-3 hover:opacity-90 transition-opacity"
            title="Go to home"
          >
            <img src="/logo-1.webp" alt="ChimneySweeps" className="w-9 h-9" />
            <div className="text-left leading-tight">
              <div className="text-lg font-bold">ChimneySweeps</div>
              <div className="text-xs text-white/70">Professional Report Builder</div>
            </div>
          </button>
          <div className="flex items-center gap-4">
            <span className="hidden sm:flex items-center gap-1.5 text-sm text-white/80">
              <Icon.Zap className="w-4 h-4" /> Quick Report Generator
            </span>
          </div>
        </div>
      </header>

      <div className="flex-1 flex min-h-0">
        {/* Sidebar - the only place page navigation lives */}
        {showSidebar && (
          <aside className="w-56 bg-white border-r border-gray-200 flex flex-col shrink-0">
            <nav className="flex-1 p-3 space-y-1">
              {NAV_ITEMS.map((item) => {
                const active = path === item.to;
                const ItemIcon = item.icon;
                return (
                  <button
                    key={item.to}
                    onClick={() => navigate(item.to)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                      active ? 'bg-[#FBEEED] text-[#722420] font-semibold' : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <ItemIcon className="w-5 h-5" />
                    <span className="flex-1 text-left">{item.label}</span>
                    {item.to === '/webhook' && webhookUnreadCount > 0 && (
                      <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-[#722420] text-white text-[11px] font-semibold flex items-center justify-center">
                        {webhookUnreadCount > 99 ? '99+' : webhookUnreadCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
            <div className="p-3">
              <div className="bg-[#FBEEED] rounded-xl p-4 text-center">
                <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center mx-auto mb-2">
                  <Icon.Shield className="w-5 h-5 text-[#722420]" />
                </div>
                <p className="text-sm font-semibold text-gray-900">Trusted by Professional Chimney Sweeps</p>
                <p className="text-xs text-gray-500 mt-1">Compliant. Accurate. Professional.</p>
              </div>
            </div>
            <div className="p-3 border-t border-gray-100">
              <div className="flex items-center gap-2.5 px-1 mb-1">
                <div className="w-8 h-8 rounded-full bg-[#FBEEED] text-[#722420] flex items-center justify-center text-xs font-semibold flex-shrink-0">
                  {initials}
                </div>
                <span className="text-sm text-gray-700 truncate flex-1" title={userEmail}>{displayName}</span>
                <button
                  onClick={async () => {
                    await logger.logLogout(userEmail);
                    await supabase.auth.signOut({ scope: 'local' });
                  }}
                  title="Logout"
                  className="p-1.5 rounded-lg text-gray-400 hover:text-[#722420] hover:bg-[#FBEEED] transition-colors flex-shrink-0"
                >
                  <Icon.LogOut className="w-4 h-4" />
                </button>
              </div>
            </div>
          </aside>
        )}

        {/* Main Content */}
        <main className="flex-1 min-w-0 px-3 sm:px-5 lg:px-8 py-4 sm:py-6 overflow-y-auto">
          {isAuthed === null ? (
            <div className="text-center text-gray-600">Loading…</div>
          ) : (!isAuthed || otpPending) ? (
            <Login />
          ) : path === '/logs' ? (
            <Logs userEmail={userEmail} />
          ) : path === '/library' ? (
            <Library userEmail={userEmail} />
          )
          : path === '/webhook' ? (
            <Webhook userEmail={userEmail} />
          ) : (
            <>
              {formStep === 'scrape' && (
                <div className="mb-4 rounded-2xl bg-gradient-to-r from-[#FBEEED] to-white border border-[#722420]/10 p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-6">
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-[#722420] tracking-wide mb-2">WELCOME TO CHIMNEYSWEEPS</p>
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                      Create Professional Chimney Inspection Reports
                    </h2>
                    <p className="text-sm text-gray-600 max-w-xl">
                      Extract data from approved sources, generate compliant reports, and maintain the
                      highest standards of accuracy and quality.
                    </p>
                  </div>
                  <div className="flex items-center gap-3 bg-white rounded-xl border border-gray-200 p-4 shadow-sm shrink-0">
                    <div className="w-12 h-12 rounded-full bg-[#FBEEED] flex items-center justify-center flex-shrink-0">
                      <Icon.Shield className="w-6 h-6 text-[#722420]" />
                    </div>
                    <div className="text-sm text-gray-700 space-y-0.5">
                      <div className="font-medium">Accurate Data</div>
                      <div className="font-medium">Better Reports</div>
                      <div className="font-medium">Happy Customers</div>
                    </div>
                  </div>
                </div>
              )}
              <MultiStepForm userEmail={userEmail} webhookData={webhookData} onStepChange={setFormStep} />
            </>
          )}
        </main>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 shrink-0">
        <div className="w-full py-4 sm:py-6">
          <div className="text-center">
            <p className="text-xs sm:text-sm text-gray-500">
              © 2024 ChimneySweeps. Professional business reporting made simple.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
